import React, { useEffect, useRef } from 'react';

const CompanyMilestone1 = () => {
  const milestones = [
    {
      year: '2019',
      title: 'Company Establishment',
      icon: 'bi-buildings',
      description: 'Foundation of Sensetech Trading LLC with a vision to revolutionize security and retail technology.',
      color: '#003366'
    },
    {
      year: '2021',
      title: 'Market Expansion',
      icon: 'bi-graph-up-arrow',
      description: 'Extended operations across Dubai and secured partnerships with leading global brands.',
      color: '#0077b6'
    },
    {
      year: '2022',
      title: 'Technology Innovation',
      icon: 'bi-gear-wide-connected',
      description: 'Launched cutting-edge RFID solutions and advanced security systems for retail.',
      color: '#00b4d8'
    },
    {
      year: '2024',
      title: 'Digital Transformation',
      icon: 'bi-globe',
      description: 'Integrated AI and cloud solutions into our service offerings for enhanced customer experience.',
      color: '#48cae4'
    },
    {
      year: '2025',
      title: 'Industry Leadership',
      icon: 'bi-trophy',
      description: 'Projected to become the leading provider of retail technology solutions globally.',
      color: '#4CAF50'
    }
  ];

  const timelineRef = useRef(null);
  const milestoneItemsRef = useRef([]);
  const yearArrowsRef = useRef([]);

  // Initialize ref arrays
  useEffect(() => {
    milestoneItemsRef.current = milestoneItemsRef.current.slice(0, milestones.length);
    yearArrowsRef.current = yearArrowsRef.current.slice(0, milestones.length);
  }, [milestones]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate milestone items
            milestoneItemsRef.current.forEach((item, index) => {
              if (item) {
                setTimeout(() => {
                  item.style.opacity = "1";
                  item.style.transform = index % 2 === 0 ? "translateY(0)" : "translateY(0)";
                }, index * 200);
              }
            });

            // Animate year arrows
            yearArrowsRef.current.forEach((arrow, index) => {
              if (arrow) {
                setTimeout(() => {
                  arrow.style.opacity = "1";
                  arrow.style.transform = "translateX(0)";
                }, 300 + index * 200);
              }
            });
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px"
      }
    );

    if (timelineRef.current) {
      observer.observe(timelineRef.current);
    }

    return () => {
      if (timelineRef.current) {
        observer.unobserve(timelineRef.current);
      }
    };
  }, []);

  return (
    <section className="py-5 timeline-section" ref={timelineRef}>
      <div className="container py-lg-5">
        <div className="row mb-5">
          <div className="col-12 text-center">
            <span className="badge rounded-pill px-3 py-2 mb-3" style={{backgroundColor:"#3ec63e"}}>Our Journey</span>
            <h2 className="display-7 fw-bold mb-4">Company Milestones</h2>
            <div className="d-flex justify-content-center">
              <div className="border-bottom border-success border-3 w-25 mb-4" ></div>
            </div>
            <p className="lead text-muted mx-auto" style={{ maxWidth: '800px', fontFamily:"Poppins" }}>
              Discover how Sensetech Trading LLC Solutions has evolved over the years to become a leading provider of security and retail technology solutions.
            </p>
          </div>
        </div>

        {/* Timeline arrow structure */}
        <div className="position-relative mt-0 pt-0">
          {/* Top milestone items */}
          <div className="row justify-content-center mb-0">
            {milestones.map((milestone, index) => (
              index % 2 === 0 && (
                <div
                  key={`top-${index}`}
                  ref={el => milestoneItemsRef.current[index] = el}
                  className="col-md-4 col-lg-2 px-4 mb-5 milestone-item"
                  style={{
                    opacity: 0,
                    transform: "translateY(20px)",
                    transition: "all 0.5s ease",
                  }}
                >
                  <div className="border-start border-3 ps-3" style={{ borderColor: milestone.color }}>
                    <div className="text-center text-md-start">
                      <div className="d-inline-block mb-3 p-3 rounded-circle bg-light">
                        <i className={`bi ${milestone.icon} fs-3`} style={{ color: milestone.color }}></i>
                      </div>
                      <h4 className="fw-bold" style={{ color: milestone.color }}>{milestone.title}</h4>
                      <p className="text-muted small">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="timeline-connector d-none d-md-block" 
                      style={{ 
                        position: 'absolute',
                        height: '40px',
                        width: '2px',
                        backgroundColor: milestone.color,
                        bottom: '-40px',
                        left: '50%',
                        zIndex: 1
                      }}>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Arrow timeline */}
          <div className="d-flex justify-content-center">
            <div className="position-relative" style={{ height: '80px', width: '95%', maxWidth: '1200px' }}>
              {/* Left arrow start */}
              <div className="position-absolute" style={{ 
                height: '80px', 
                width: '40px', 
                left: '-40px',
                backgroundImage: 'linear-gradient(to right, #f8f9fa, #f8f9fa)',
                clipPath: 'polygon(100% 0, 0% 50%, 100% 100%)'
              }}></div>
              
              {/* Main arrow sections */}
              {milestones.map((milestone, index) => (
                <div
                  key={`arrow-${index}`}
                  ref={el => yearArrowsRef.current[index] = el}
                  className="year-arrow position-absolute"
                  style={{ 
                    height: '80px',
                    left: `${index * (100 / milestones.length)}%`,
                    width: `${100 / milestones.length}%`,
                    backgroundColor: milestone.color,
                    clipPath: 'polygon(0% 0%, 90% 0%, 100% 50%, 90% 100%, 0% 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transform: 'translateX(20px)',
                    transition: 'all 0.5s ease'
                  }}
                >
                  <h2 className="text-white fw-bold m-0">{milestone.year}</h2>
                </div>
              ))}
              
              {/* Right arrow end */}
              <div className="position-absolute" style={{ 
                height: '80px', 
                width: '40px', 
                right: '-40px',
                backgroundColor: '#4CAF50',
                clipPath: 'polygon(0 0, 100% 50%, 0 100%)'
              }}></div>
            </div>
          </div>

          {/* Bottom milestone items */}
          <div className="row justify-content-center mt-0">
            {milestones.map((milestone, index) => (
              index % 2 !== 0 && (
                <div
                  key={`bottom-${index}`}
                  ref={el => milestoneItemsRef.current[index] = el}
                  className="col-md-4 col-lg-2 px-4 mt-5 milestone-item"
                  style={{
                    opacity: 0,
                    transform: "translateY(-20px)",
                    transition: "all 0.5s ease",
                  }}
                >
                  <div className="timeline-connector d-none d-md-block" 
                      style={{ 
                        position: 'absolute',
                        height: '40px',
                        width: '2px',
                        backgroundColor: milestone.color,
                        top: '-40px',
                        left: '50%',
                        zIndex: 1
                      }}>
                  </div>
                  <div className="border-start border-3 ps-3" style={{ borderColor: milestone.color }}>
                    <div className="text-center text-md-start">
                      <div className="d-inline-block mb-3 p-3 rounded-circle bg-light">
                        <i className={`bi ${milestone.icon} fs-3`} style={{ color: milestone.color }}></i>
                      </div>
                      <h4 className="fw-bold" style={{ color: milestone.color }}>{milestone.title}</h4>
                      <p className="text-muted small">{milestone.description}</p>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .milestone-item {
          opacity: 0;
          transition: all 0.5s ease;
        }
        .year-arrow {
          opacity: 0;
          transition: all 0.5s ease;
        }
      `}</style>
    </section>
  );
};

export default CompanyMilestone1;